import { useFonts } from '@/composables/fonts';
import fontManagerService from '@/utils/fontManagerService';
import { fetchFontFaceMap, downloadFontFile } from '@/utils/amazonFontResolver';
import type { CanvaFont } from '@/typings/layers/layers';

export interface ResolveAmazonFontsResult {
    resolved: CanvaFont[];
    stillMissing: CanvaFont[];
    // rawTypeface -> name/style the server read from the uploaded file
    registered: Map<string, Pick<CanvaFont, 'family' | 'style'>>;
}

export const useAmazonFontImport = () => {
    const fonts = useFonts();

    /**
     * Downloads the .ttf for each missing font (its own fileUrl, else Amazon's fontsCssUrl) and
     * registers it as an account font (Fonts, not a Library). Fonts with no .ttf source, or that
     * fail to download/upload, are returned in `stillMissing` so the caller can fall back to the
     * manual font replacement flow.
     */
    const resolveAmazonFonts = async (
        fontsCssUrl: string | undefined,
        missingFonts: CanvaFont[]
    ): Promise<ResolveAmazonFontsResult> => {
        const resolved: CanvaFont[] = [];
        const stillMissing: CanvaFont[] = [];
        const registered: ResolveAmazonFontsResult['registered'] = new Map();
        const uploadedFileNames = new Map<string, string>();

        let fontFaceMap = new Map<string, string>();
        if (fontsCssUrl) {
            try {
                fontFaceMap = await fetchFontFaceMap(fontsCssUrl);
            } catch (error) {
                // Fonts with their own fileUrl can still resolve
                console.error('Error fetching Amazon fonts CSS:', error);
            }
        }

        for (const font of missingFonts) {
            const ttfUrl = font.fileUrl || fontFaceMap.get(font.family);
            if (!ttfUrl) {
                stillMissing.push(font);
                continue;
            }

            try {
                const file = await downloadFontFile(ttfUrl, `${font.family}.ttf`);
                uploadedFileNames.set(font.rawTypeface, await fonts.uploadUserFont(file));
                resolved.push(font);
            } catch (error) {
                console.error(`Error resolving Amazon font "${font.family}":`, error);
                stillMissing.push(font);
            }
        }

        if (resolved.length) {
            // Refresh both the FontManagerService singleton cache (used by the legacy Angular
            // create-product controller) and the Vuex store (used by Vue components) so the
            // freshly uploaded fonts are immediately matchable during this same import.
            const userFonts = await fontManagerService.getAllFonts(true);

            // The server names fonts from the file itself, which can differ from Amazon's label
            uploadedFileNames.forEach((fileName, rawTypeface) => {
                const entry = userFonts.find((f) => f.Path.replace(/\\/g, '/').endsWith(`/${fileName}`));
                if (entry) registered.set(rawTypeface, { family: entry.Name, style: entry.Style });
            });
        }

        return { resolved, stillMissing, registered };
    };

    return { resolveAmazonFonts };
};
